import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import TreasuryABI from '@/abis/Treasury.json';
import { ethers, EventLog, Result } from 'ethers';
import { API_BASE_URL } from '@/config';
import { Loader2, Search } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';

interface Transaction {
  id: string;
  type: 'Deposit' | 'Withdrawal' | 'Operational' | 'Grant';
  description: string;
  from: string;
  to: string;
  amount: string;
  currency: 'ETH' | 'USD' | string;
  timestamp: Date;
}

const TreasuryRealDataTable: React.FC = () => {
  const { provider, isConnected } = useWallet();
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const { notify } = useToast();
  const { addLog } = useLogger();
  const PAGE_SIZE = 15;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    addLog("Starting treasury data fetch.", LogLevel.INFO, LogCategory.DATA);

    try {
      let onChainTxs: Transaction[] = [];
      let offChainTxs: Transaction[] = [];

      // 1. Fetch On-Chain (Crypto) Transactions if wallet is connected
      if (provider && isConnected) {
        try {
          const treasuryContract = new ethers.Contract(contractAddresses.Treasury, TreasuryABI.abi, provider);
          const [depositEvents, withdrawalEvents] = await Promise.all([
              treasuryContract.queryFilter(treasuryContract.filters.FundsDeposited()),
              treasuryContract.queryFilter(treasuryContract.filters.FundsWithdrawn())
          ]);

          const processEvents = (events: EventLog[], type: 'Deposit' | 'Withdrawal'): Promise<Transaction[]> => 
            Promise.all(events.map(async (event: EventLog) => {
              const block = await event.getBlock();
              const args = event.args as Result;
              return {
                id: event.transactionHash,
                type,
                description: type === 'Deposit' ? 'On-chain deposit' : 'On-chain withdrawal',
                from: args.from,
                to: type === 'Withdrawal' ? args.to : contractAddresses.Treasury,
                amount: ethers.formatEther(args.amount),
                currency: 'ETH',
                timestamp: new Date(block.timestamp * 1000),
              };
            }));
          
          onChainTxs = [...await processEvents(depositEvents as EventLog[], 'Deposit'), ...await processEvents(withdrawalEvents as EventLog[], 'Withdrawal')];
          addLog(`Fetched ${onChainTxs.length} on-chain transactions.`, LogLevel.INFO, LogCategory.DATA);
        } catch (e: unknown) {
          const onChainError = "Could not load on-chain data. Check wallet connection or contract status.";
          addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
          setError(onChainError);
          notify(onChainError, 'error');
        }
      }

      // 2. Fetch Off-Chain (Fiat) Transactions with Robust Mock Fallback
      try {
        let apiData;
        const response = await fetch(`${API_BASE_URL}/api/fiat-transactions`, { credentials: 'include' });

        const contentType = response.headers.get("content-type");
        if (response.ok && contentType && contentType.includes("application/json")) {
          apiData = await response.json();
          addLog(`Fetched ${apiData.length} off-chain transactions from REAL API.`, LogLevel.INFO, LogCategory.API);
        } else {
          addLog(`Real API failed or did not return JSON. Falling back to mock data.`, LogLevel.WARN, LogCategory.API);
          const mockResponse = await fetch('/treasury_transactions.json');
          if (mockResponse.ok) {
            apiData = await mockResponse.json();
            addLog(`Loaded ${apiData.length} transactions from MOCK file.`, LogLevel.INFO, LogCategory.DATA);
            notify('Displaying mock data. Could not connect to the real API.', 'warn');
          } else {
            throw new Error(`Real API failed and mock data file could not be loaded.`);
          }
        }
        
        if (Array.isArray(apiData)) {
            offChainTxs = apiData.map((tx: Record<string, unknown>): Transaction => {
                const description = (tx.description as string || '').toLowerCase();
                let type: Transaction['type'];
                if (tx.type === 'INCOME') {
                    type = description.includes('grant') ? 'Grant' : 'Deposit';
                } else {
                    type = description.includes('withdraw') ? 'Withdrawal' : 'Operational';
                }

                return {
                    id: (tx.id as string) || `json-${Math.random()}`,
                    type,
                    description: (tx.description as string) || 'Fiat Transaction',
                    from: tx.type === 'INCOME' ? 'External' : 'Treasury',
                    to: tx.type === 'EXPENSE' ? 'External' : 'Treasury',
                    amount: String(tx.amount),
                    currency: (tx.currency as string) || 'USD',
                    timestamp: new Date(tx.createdAt as string || tx.timestamp as string),
                };
            });
        } else {
            throw new Error('Fetched off-chain data is not an array.');
        }

      } catch (e: unknown) {
        const offChainError = `Critical: Failed to load off-chain ledger data: ${(e as Error).message}`;
        addLog(offChainError, LogLevel.CRITICAL, LogCategory.API);
        setError(prev => prev ? `${prev} | ${offChainError}` : offChainError);
        notify(offChainError, 'error');
      }
      
      const all = [...onChainTxs, ...offChainTxs].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
      setAllTransactions(all);

    } catch (e: unknown) {
      const genericError = `Failed to load ledger data: ${(e as Error).message}`;
      setError(genericError);
      addLog(genericError, LogLevel.ERROR, LogCategory.DATA);
    } finally {
      setIsLoading(false);
    }
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredTransactions = useMemo(() => {
      return allTransactions.filter(tx => 
          tx.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tx.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tx.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tx.to.toLowerCase().includes(searchTerm.toLowerCase())
      );
  }, [allTransactions, searchTerm]);

  const displayedTransactions = useMemo(() => {
      return filteredTransactions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  }, [filteredTransactions, page]);

  const hasMore = page * PAGE_SIZE < filteredTransactions.length;

  const truncateAddress = (addr: string) => addr.startsWith('0x') ? `${addr.substring(0, 6)}...${addr.substring(addr.length - 4)}` : addr;

  const typeColors: { [key in Transaction['type']]: string } = {
    Deposit: 'bg-green-500/20 text-green-300',
    Withdrawal: 'bg-red-500/20 text-red-300',
    Operational: 'bg-blue-500/20 text-blue-300',
    Grant: 'bg-purple-500/20 text-purple-300',
  }

  if (isLoading && allTransactions.length === 0) return (
    <div className="flex justify-center items-center h-48">
        <Loader2 className="w-8 h-8 text-kala-secondary animate-spin" />
        <p className="ml-4 text-kala-400">Loading Unified Treasury Ledger...</p>
    </div>
  );

  return (
    <div className="bg-kala-900 text-white rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Unified Treasury Ledger</h2>
            <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-kala-500" />
                <input
                    type="text"
                    placeholder="Search ledger (ID, address, desc)..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="bg-kala-800 border border-kala-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:ring-1 focus:ring-kala-secondary outline-none w-72"
                />
            </div>
        </div>

        {error && <div className="p-4 mb-4 bg-yellow-500/10 text-yellow-400 text-center text-sm rounded-lg">Warning: {error}</div>}
        {!isConnected && (
            <div className="p-2 mb-4 bg-kala-900/30 text-kala-500 text-center text-[10px] uppercase tracking-widest border-y border-kala-700">
            Note: Wallet disconnected. On-chain data is unavailable.
            </div>
        )}

        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-kala-700">
                <thead className="bg-kala-900/50">
                    <tr>
                    {['Timestamp', 'Type', 'Description', 'Amount', 'Transaction ID / Source'].map(header => (
                        <th key={header} scope="col" className="px-6 py-3 text-left text-xs font-medium text-kala-300 uppercase tracking-wider">
                        {header}
                        </th>
                    ))}
                    </tr>
                </thead>
                <tbody className="bg-kala-800 divide-y divide-kala-700">
                    {displayedTransactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-kala-700/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-kala-400">{tx.timestamp.toLocaleDateString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${typeColors[tx.type]}`}>
                            {tx.type}
                        </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-kala-300">{tx.description}</td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-mono ${tx.currency === 'ETH' ? 'text-green-400' : 'text-kala-200'}`}>
                        {(tx.type === 'Deposit' || tx.type === 'Grant' ? '+' : '-')}{tx.amount} {tx.currency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-kala-500 text-right font-mono">
                        {tx.currency === 'ETH' ? (
                            <a href={`https://sepolia.etherscan.io/tx/${tx.id}`} target="_blank" rel="noopener noreferrer" className="hover:text-kala-300 underline decoration-kala-700">
                            {truncateAddress(tx.id)}
                            </a>
                        ) : (
                            <span>Fiat / {tx.id.substring(0, 8)}</span>
                        )}
                        </td>
                    </tr>
                    ))}
                </tbody>
            </table>
        </div>

        {displayedTransactions.length === 0 && !isLoading && (
            <div className="text-center p-8 text-kala-500">
                <p>No transactions found for your search term.</p>
            </div>
        )}

        <div className="flex justify-between items-center mt-6">
            <button 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1 || isLoading}
            className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
            >
            Previous
            </button>
            <span className="text-kala-400">Page {page} of {Math.ceil(filteredTransactions.length / PAGE_SIZE)}</span>
            <button 
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore || isLoading}
            className="px-4 py-2 bg-kala-700 hover:bg-kala-600 text-white font-bold rounded-lg disabled:opacity-50 transition-colors"
            >
            Next
            </button>
      </div>
    </div>
  );
};

export default TreasuryRealDataTable;
